import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { parseManualDOB } from "@/lib/aamva";
import { useIdScanListener, parseScanRaw } from "@/hooks/useIdScanListener";

// Age verification dialog. Opens BEFORE an age-restricted item is added to
// the cart. States: waiting → pass (green, 4s auto-close, item added) OR
// fail-underage (red, 3s auto-close, item NOT added) OR fail-expired (red,
// 3s auto-close, item NOT added) OR invalid (yellow, stays open). Skip
// closes the dialog and adds the item with an audit log entry.
//
// Props:
//   open          bool
//   requiredAge   18 | 21
//   onPass(info)  called with { name, dob, age, method: "scan"|"manual", state?, license_no_last4? }
//   onFail()      customer under-age or expired — do not add item
//   onSkip()      cashier bypassed verification — add item, log skip
//   onClose()     dismiss (used by ✕ button)
//   verifiedAge   number|null — if already known from a prior scan this txn,
//                 the dialog opens straight to a "block" message when the
//                 known age < requiredAge (no re-prompt).
//   verifiedName  string|null — echoed in the block message.

export default function AgeCheckDialog({
  open,
  requiredAge = 21,
  onPass,
  onFail,
  onSkip,
  onClose,
  verifiedAge = null,
  verifiedName = null,
}) {
  const [phase, setPhase] = useState("waiting"); // waiting | pass | fail | expired | invalid | block
  const [info, setInfo] = useState(null);
  const [manual, setManual] = useState("");
  const closeTimerRef = useRef(null);
  // Latest info so timers/handlers always fire the current data
  const infoRef = useRef(null);
  const phaseRef = useRef("waiting");
  useEffect(() => { infoRef.current = info; }, [info]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Reset when reopened
  useEffect(() => {
    if (!open) return undefined;
    // If we already know the age from an earlier verification, decide up-front
    if (verifiedAge != null) {
      if (verifiedAge >= requiredAge) {
        // Shouldn't reach here — caller should have added silently
        onPass?.({ age: verifiedAge, name: verifiedName, dob: null, method: "session" });
        onClose?.();
        return undefined;
      }
      setInfo({ age: verifiedAge, name: verifiedName });
      setPhase("block");
      closeTimerRef.current = setTimeout(() => {
        onFail?.(); onClose?.();
      }, 3000);
    } else {
      setPhase("waiting");
      setInfo(null);
      setManual("");
    }
    return () => {
      clearTimeout(closeTimerRef.current);
    };
    // eslint-disable-next-line
  }, [open]);

  const finishPass = (data) => {
    setInfo(data);
    setPhase("pass");
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      onPass?.(data); onClose?.();
    }, 4000);
  };
  const finishFail = (data, reason = "fail") => {
    setInfo(data);
    setPhase(reason);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      onFail?.(); onClose?.();
    }, 3000);
  };

  const handleScan = (raw) => {
    const parsed = parseScanRaw(raw);
    if (!parsed) {
      // Diagnostic: log the raw scan so we can see exactly what this
      // scanner/keyboard-wedge config transmits if parsing keeps failing.
      // eslint-disable-next-line no-console
      console.warn(
        "[AgeCheck] Failed to parse scan as AAMVA. Raw length:", raw.length,
        "\nRaw (escaped):", JSON.stringify(raw),
        "\nChar codes (first 60):", Array.from(raw.slice(0, 60)).map((c) => c.charCodeAt(0)).join(","),
      );
      setPhase("invalid");
      return;
    }
    const name = parsed.full_name || `${parsed.first_name} ${parsed.last_name}`.trim();
    const dobStr = parsed.dob?.toLocaleDateString("en-US");
    const license_no_last4 = parsed.license_no ? parsed.license_no.slice(-4) : null;
    if (parsed.is_expired) {
      finishFail({ name, dob: dobStr, age: parsed.age, expDate: parsed.expiration?.toLocaleDateString("en-US") }, "expired");
      return;
    }
    if (parsed.age >= requiredAge) {
      finishPass({ name, dob: dobStr, age: parsed.age, method: "scan", state: parsed.state, license_no_last4 });
    } else {
      finishFail({ name, dob: dobStr, age: parsed.age }, "fail");
    }
  };

  // Global scan listener — accumulates fast keystrokes and detects AAMVA (extracted to
  // src/hooks/useIdScanListener.js).
  useIdScanListener({ open, phase, onScan: handleScan });

  const handleManualVerify = () => {
    const r = parseManualDOB(manual);
    if (!r) { setPhase("invalid"); return; }
    const dobStr = r.dob.toLocaleDateString("en-US");
    if (r.age >= requiredAge) {
      finishPass({ name: "(manual entry)", dob: dobStr, age: r.age, method: "manual" });
    } else {
      finishFail({ name: "(manual entry)", dob: dobStr, age: r.age }, "fail");
    }
  };

  // Close dispatcher — fires the CORRECT callback based on current phase so
  // that clicking OK / ✕ during PASS still adds the item to cart & sets
  // session age. During FAIL/EXPIRED/BLOCK, it triggers onFail. During
  // WAITING, it's an unattended dismiss — behaves like Skip? No — the safest
  // behavior is to abort silently (item NOT added), which is what onClose alone
  // implies. INVALID also just closes (cashier can retry).
  const handleClose = () => {
    clearTimeout(closeTimerRef.current);
    const p = phaseRef.current;
    if (p === "pass" && infoRef.current) {
      onPass?.(infoRef.current);
    } else if (p === "fail" || p === "expired" || p === "block") {
      onFail?.();
    }
    onClose?.();
  };

  if (!open) return null;

  const color = phase === "pass" ? "green" : phase === "invalid" ? "yellow" : phase === "waiting" ? "blue" : "red";
  const colors = {
    green: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", icon: <CheckCircle2 className="w-8 h-8 text-emerald-600" /> },
    red: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", icon: <AlertTriangle className="w-8 h-8 text-red-600" /> },
    yellow: { bg: "bg-yellow-50", border: "border-yellow-300", text: "text-yellow-700", icon: <AlertTriangle className="w-8 h-8 text-yellow-600" /> },
    blue: { bg: "bg-white", border: "border-zinc-200", text: "text-zinc-800", icon: <Loader2 className="w-10 h-10 text-blue-600 animate-spin" /> },
  }[color];

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.5)" }} className="flex items-center justify-center">
      <div className={`${colors.bg} ${colors.border} border-2 rounded-lg shadow-2xl w-[440px] p-5 relative`} data-testid="age-check-dialog">
        <button type="button" onClick={handleClose} className="absolute top-2 right-2 text-zinc-400 hover:text-zinc-800" data-testid="age-check-close">
          <X className="w-4 h-4" />
        </button>
        <div className="text-sm font-bold uppercase tracking-widest text-zinc-500 mb-2">Age Verification Required — {requiredAge}+</div>

        {phase === "waiting" && (
          <>
            <div className="flex flex-col items-center py-4">
              {colors.icon}
              <div className="mt-2 text-base font-semibold text-zinc-800">Please Scan Customer ID</div>
              <div className="text-xs text-zinc-500 mt-1">Waiting for scan…</div>
            </div>
            <div className="text-center text-[10px] uppercase text-zinc-400 my-2">— or enter manually —</div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-zinc-600 whitespace-nowrap">DOB (MMDDYY):</label>
              <input
                value={manual}
                onChange={(e) => setManual(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && handleManualVerify()}
                className="flex-1 border border-zinc-300 rounded px-2 py-1.5 text-sm font-mono focus:ring-2 focus:ring-blue-400"
                placeholder="MMDDYY"
                data-calc="0"
                data-testid="age-manual-dob"
              />
              <button
                type="button"
                onClick={handleManualVerify}
                disabled={manual.length !== 6}
                className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded font-semibold disabled:opacity-40"
                data-testid="age-manual-verify"
              >
                Verify
              </button>
            </div>
            <div className="mt-4 text-right">
              <button type="button" onClick={() => { onSkip?.(); onClose?.(); }} className="text-xs px-3 py-1.5 border rounded text-zinc-600 hover:bg-zinc-50" data-testid="age-check-skip">
                Skip
              </button>
            </div>
          </>
        )}

        {phase === "pass" && info && (
          <div className="flex flex-col items-center py-4" data-testid="age-check-pass">
            {colors.icon}
            <div className={`mt-2 text-xl font-black ${colors.text}`}>Verified — {info.age} years old</div>
            <div className="mt-1 text-sm font-semibold text-zinc-800">{info.name}</div>
            <div className="text-xs text-zinc-500">DOB: {info.dob}</div>
            <button type="button" onClick={handleClose} className="mt-3 px-6 py-1.5 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700" data-testid="age-check-ok">
              OK
            </button>
            <div className="text-[10px] text-zinc-400 mt-1">Closes in 4s…</div>
          </div>
        )}

        {(phase === "fail" || phase === "block") && info && (
          <div className="flex flex-col items-center py-4" data-testid={`age-check-${phase}`}>
            {colors.icon}
            <div className={`mt-2 text-xl font-black ${colors.text}`}>Under {requiredAge} — {info.age} years old</div>
            {info.name && <div className="mt-1 text-sm font-semibold text-zinc-800">{info.name}</div>}
            {info.dob && <div className="text-xs text-zinc-500">DOB: {info.dob}</div>}
            {phase === "block" && <div className="text-xs text-red-600 mt-1">(from earlier ID scan this sale)</div>}
            <div className="text-[10px] text-red-600 mt-2 font-semibold uppercase">Item removed from sale</div>
          </div>
        )}

        {phase === "expired" && info && (
          <div className="flex flex-col items-center py-4" data-testid="age-check-expired">
            {colors.icon}
            <div className={`mt-2 text-xl font-black ${colors.text}`}>ID Expired</div>
            <div className="text-xs text-red-600">Expired {info.expDate}</div>
            <div className="mt-2 text-sm font-semibold text-zinc-800">{info.name}</div>
            <div className="text-xs text-zinc-500">DOB: {info.dob}</div>
            <div className="text-[10px] text-red-600 mt-2 font-semibold uppercase">Item removed from sale</div>
          </div>
        )}

        {phase === "invalid" && (
          <div className="flex flex-col items-center py-4" data-testid="age-check-invalid">
            {colors.icon}
            <div className={`mt-2 text-lg font-bold ${colors.text}`}>Not a valid ID</div>
            <div className="text-xs text-zinc-500 mt-1">Please scan the back of the license, or enter DOB manually.</div>
            <button type="button" onClick={() => { setPhase("waiting"); setManual(""); }} className="mt-3 px-4 py-1.5 border rounded text-xs" data-testid="age-check-retry">
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
